/// <amd-module name="TFS/Admin/Views/OrganizationAad/OrganizationAad" />
import React = require("react");
import { AdminTelemetry, AdminFeatureTelemetryLogger } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";
import { Components, VssComponent } from "VSS/Platform/Layout";
import { IVssContributionService } from "VSS/Platform/Contribution";
import AdminResources = require("TFS/Admin/Views/OrganizationAad/Resources");
import { Button } from "VSSUI/Button";
import { Icon } from "VSSUI/Icon";
import { EmptyGuidString, format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { Link } from "VSSUI/Link";
import { TextField } from "VSSUI/TextField";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import {
    IOrganizationAadData,
    IUserMappingData,
    IUserTenantData,
    IUserTenantsData,
    IOrganizationTokenData,
    IAadTenantPolicy,
    IOrganizationAadDelayLoadSettingsData,
    IAadObject
} from "TFS/Admin/OrganizationAadCommon/Contracts/DataModels";
import { Area, Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { OrganizationAadSource } from "TFS/Admin/OrganizationAadCommon/Sources/OrganizationAadSource";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { filterXSS } from "xss";
import { createMarkup } from "TFS/Admin/Common/Util/HtmlString";
import { DisconnectedUsers } from "TFS/Admin/Common/Contracts/OrganizationSettings";
import { IdentityRefWithEmail } from "VSS/Platform/Contracts/WebApi";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Invitee, InviteeStatus } from "TFS/Admin/Common/Contracts/Invitation";
import { CleanErrorMessage, ParseErrorFromAPI } from "TFS/Admin/Common/Util/CustomErrorHelper";
import { WrappedComponent } from "VSS/Platform/Layout";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IOperationStatus } from "TFS/Admin/Common/Constants/Constants";
import { Toggle } from "VSSUI/Toggle";
import { ObservableValue } from "VSS/Core/Observable";
import { IIdentity } from "VSSUI/IdentityPicker";
import { AadGroupOrUserPillComponent } from "TFS/Admin/Views/OrganizationAad/Components/AadGroupOrUserPill";
import { PillGroup } from "VSSUI/PillGroup";
import { IMultiOperationDialogProps } from "TFS/Admin/Common/Controls/MultiOperationDialog/MultiOperationDialog.Props";
import {
    IAadResolveLoadingPanelComponentProps,
    IAadResolvePanelComponentProps,
    IAadSendInvitePanelComponentProps,
    IAadConnectPanelComponentProps,
    IAadDisconnectDialogComponentProps
} from "TFS/Admin/OrganizationAadCommon/Contracts/ComponentControls.props";
import {
    IAadActionSuccessDialogComponentProps,
    IAadActionUnsuccessDialogComponentProps
} from "TFS/Admin/OrganizationAadCommon/Contracts/DialogControls.props";
import {
    IAddAadGroupPanelComponentProps,
    IEditDisplayMessagePanelComponentProps
} from "TFS/Admin/OrganizationAadCommon/Contracts/AadPolicyControls.props";
import { IUsersListDialogComponentProps } from "TFS/Admin/Common/Controls/UsersListDialog/UserListDialog.props";
import { PolicyNames, PolicyProperties } from "TFS/Admin/Common/Constants/Generated/Constants";

const dataProviderId = "ms.vss-admin-web.organization-admin-aad-data-provider";
const delayLoadProviderId = "ms.vss-admin-web.organization-admin-aad-delay-load-data-provider";
const userTenantDataProviderId = "ms.vss-admin-web.organization-admin-aad-user-tenants-data-provider";
const authTokenDataProviderId = "ms.vss-admin-web.organization-admin-overview-token-data-provider";

const enforceTokenLifespanPolicyDefaultMaxLifespanDays = 30;

interface IOrganizationAadComponentState {
    data: IOrganizationAadData;
    showAadConnectPanel: boolean;
    updateAadInProgress: boolean;
    showAadDisconnectDialog: boolean;
    showAadDisconnectSuccessDialog: boolean;
    showAadConnectSuccessDialog: boolean;
    showAadDisconnectUnsuccessDialog: boolean;
    showAadConnectUnsuccessDialog: boolean;
    aadConnectError: string;
    aadDisconnectError: string;
    newOrgTenantName: string;
    disconnectedUsers: IdentityRefWithEmail[];
    showDisconnectUserDialog: boolean;
    showResolvePanel: boolean;
    showSendInvitePanel: boolean;
    totalOrgUsers: number;
    showTooManyUsersWarning: boolean;
    showMappingLoadingPanel: boolean;
    showInvitationLoadingPanel: boolean;
    mappingUserInProgress: number;
    numberOfSuccessfulMappedUser: number;
    numberOfUnsuccessfulMappedUser: number;
    numberOfSuccessfulInvitedUser: number;
    numberOfUnsuccessfulInvitedUser: number;
    userResolveActionDone: boolean;
    showDisconnectedUserWarning: boolean;
    failureMappingUsers: IdentityRefWithEmail[];
    failureMappingUsersCustomModel: IOperationStatus[];
    showFailureMappedUsersDialog: boolean;
    showFailureInvitingUsersDialog: boolean;
    hasUserTenant: boolean;
    isDownloadInProgress: boolean;
    showDownloadErrorMessageBar: boolean;
    downloadErrorMessage: string;
    userTenantData?: IUserTenantData;
    showOrgCreationAddAadGroupPanel: boolean;
    showGlobalPatAddAadGroupPanel: boolean;
    showFullScopePatAddAadGroupPanel: boolean;
    showTokenLifespanAddAadGroupPanel: boolean;
    showEditErrorMessagePanel: boolean;
    orgPolicyData?: IAadTenantPolicy;
    currentPolicyData?: IAadTenantPolicy;
    globalPatPolicyData?: IAadTenantPolicy;
    fullScopePatPolicyData?: IAadTenantPolicy;
    tokenLifespanPolicyData?: IAadTenantPolicy;
    tokenAutoRevocationPolicyData?: IAadTenantPolicy;
    isOrgPolicyUpdateInProgress: boolean;
    isGlobalPatPolicyUpdateInProgress: boolean;
    isFullScopePatPolicyUpdateInProgress: boolean;
    isTokenLifespanPolicyUpdateInProgress: boolean;
    isTokenAutoRevocationPolicyUpdateInProgress: boolean;
    showOrgCreationPolicyErrorMessageBar: boolean;
    showGlobalPatPolicyErrorMessageBar: boolean;
    showFullScopePatPolicyErrorMessageBar: boolean;
    showTokenLifespanPolicyErrorMessageBar: boolean;
    showTokenAutoRevocationPolicyErrorMessageBar: boolean;
    orgCreationPolicyErrorMessage: string;
    globalPatPolicyErrorMessage: string;
    fullScopePatPolicyErrorMessage: string;
    tokenLifespanPolicyErrorMessage: string;
    tokenLifespanPendingMaxLifespanDays: string;
    tokenAutoRevocationPolicyErrorMessage: string;
}

export class OrganizationAadComponent extends VssComponent<{}, IOrganizationAadComponentState> {
    private telemetryLogger: AdminTelemetry = new AdminTelemetry(this.context.pageContext);
    private hasTenantConnected = false;
    private isOrgPolicyEnabled: boolean;
    private isGlobalPatPolicyEnabled: boolean;
    private isFullScopePatPolicyEnabled: boolean;
    private isTokenLifespanPolicyEnabled: boolean;
    private isTokenAutoRevocationPolicyEnabled: boolean;
    private isSwitchTenantEnabled: boolean;
    protected orgPolicyToggleEnabled: ObservableValue<boolean> = new ObservableValue<boolean>(false);
    protected globalPatPolicyToggleEnabled: ObservableValue<boolean> = new ObservableValue<boolean>(false);
    protected fullScopePatPolicyToggleEnabled: ObservableValue<boolean> = new ObservableValue<boolean>(false);
    protected tokenLifespanPolicyToggleEnabled: ObservableValue<boolean> = new ObservableValue<boolean>(false);
    protected tokenAutoRevocationPolicyToggleEnabled: ObservableValue<boolean> = new ObservableValue<boolean>(false);

    public constructor(props: {}, context?: any) {
        super(props, context);
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const enableOrganizationPolicy = "VisualStudio.Services.AdminEngagement.OrganizationAzureActiveDirectory.EnableOrganizationPolicy";
        const enableGlobalPatPolicy = "VisualStudio.Services.TenantPolicy.Admin.PATs.RestrictGlobalTokenCreationPolicy";
        const enableFullScopePatPolicy = "VisualStudio.Services.TenantPolicy.Admin.PATs.RestrictFullScopeTokenCreationPolicy";
        const enableTokenLifespanPolicy = "VisualStudio.Services.TenantPolicy.Admin.PATs.RestrictTokenLifespanPolicy";
        const enableTokenAutoRevocationPolicyEnabled = "VisualStudio.Services.TenantPolicy.Admin.PATs.EnableLeakedPersonalAccessTokenAutoRevocation";
        const enableSwitchTenant = "VisualStudio.Services.AdminEngagement.OrganizationAzureActiveDirectory.EnableSwitchTenant";
        var settingsData = contributionService.getData<IOrganizationAadData>(dataProviderId)!;
        if (settingsData && settingsData.orgnizationTenantData && settingsData.orgnizationTenantData.id) {
            this.hasTenantConnected = settingsData.orgnizationTenantData.id != EmptyGuidString;
        }
        this.isOrgPolicyEnabled = isFeatureFlagEnabled(this.context.pageContext, enableOrganizationPolicy, false);
        this.isGlobalPatPolicyEnabled = isFeatureFlagEnabled(this.context.pageContext, enableGlobalPatPolicy, false);
        this.isFullScopePatPolicyEnabled = isFeatureFlagEnabled(this.context.pageContext, enableFullScopePatPolicy, false);
        this.isTokenLifespanPolicyEnabled = isFeatureFlagEnabled(this.context.pageContext, enableTokenLifespanPolicy, false);        
        this.isTokenAutoRevocationPolicyEnabled = isFeatureFlagEnabled(this.context.pageContext, enableTokenAutoRevocationPolicyEnabled, false);
        this.isSwitchTenantEnabled = isFeatureFlagEnabled(this.context.pageContext, enableSwitchTenant, false);

        this.state = {
            data: settingsData,
            showAadConnectPanel: false,
            updateAadInProgress: false,
            showAadDisconnectDialog: false,
            showAadDisconnectSuccessDialog: false,
            showAadConnectSuccessDialog: false,
            showAadConnectUnsuccessDialog: false,
            showAadDisconnectUnsuccessDialog: false,
            aadConnectError: "",
            aadDisconnectError: "",
            newOrgTenantName: "",
            disconnectedUsers: [],
            showDisconnectUserDialog: false,
            showResolvePanel: false,
            showSendInvitePanel: false,
            totalOrgUsers: 0,
            showTooManyUsersWarning: false,
            showMappingLoadingPanel: false,
            showInvitationLoadingPanel: false,
            mappingUserInProgress: 0,
            numberOfSuccessfulMappedUser: 0,
            numberOfUnsuccessfulMappedUser: 0,
            numberOfSuccessfulInvitedUser: 0,
            numberOfUnsuccessfulInvitedUser: 0,
            userResolveActionDone: false,
            showDisconnectedUserWarning: false,
            failureMappingUsers: [],
            failureMappingUsersCustomModel: [],
            showFailureMappedUsersDialog: false,
            showFailureInvitingUsersDialog: false,
            hasUserTenant: false,
            isDownloadInProgress: false,
            showDownloadErrorMessageBar: false,
            downloadErrorMessage: "",
            showOrgCreationAddAadGroupPanel: false,
            showGlobalPatAddAadGroupPanel: false,
            showFullScopePatAddAadGroupPanel: false,
            showTokenLifespanAddAadGroupPanel: false,
            showEditErrorMessagePanel: false,
            isOrgPolicyUpdateInProgress: false,
            isGlobalPatPolicyUpdateInProgress: false,
            isFullScopePatPolicyUpdateInProgress: false,
            isTokenLifespanPolicyUpdateInProgress: false,
            isTokenAutoRevocationPolicyUpdateInProgress: false,
            showOrgCreationPolicyErrorMessageBar: false,
            showGlobalPatPolicyErrorMessageBar: false,
            showFullScopePatPolicyErrorMessageBar: false,
            showTokenLifespanPolicyErrorMessageBar: false,
            showTokenAutoRevocationPolicyErrorMessageBar: false,
            orgCreationPolicyErrorMessage: "",
            globalPatPolicyErrorMessage: "",
            fullScopePatPolicyErrorMessage: "",
            tokenLifespanPolicyErrorMessage: "",
            tokenAutoRevocationPolicyErrorMessage: "",
            tokenLifespanPendingMaxLifespanDays: enforceTokenLifespanPolicyDefaultMaxLifespanDays.toString()
        };
        if (this.hasTenantConnected === true && this.state.data.hasModifyPermissions === true) {
            this._onGetUnresolveUsers();
        }
    }

    public render(): JSX.Element {
        return (
            <div className="left-margin organization-aad-main-div">
                <h2 className="fontSizeL fontWeightSemiBold title-l">{AdminResources.OrganizationAadHeading}</h2>

                {this.state.hasUserTenant === false && this.hasTenantConnected === false ? this._renderNoUserTenantInfoMessage() : null}
                {this.state.userResolveActionDone === true &&
                    this.state.disconnectedUsers.length == 0 &&
                    (this.state.numberOfSuccessfulInvitedUser > 0 || this.state.numberOfSuccessfulMappedUser > 0)
                    ? this._renderUserResolveActionSuccessMessageBar()
                    : null}
                {this.state.userResolveActionDone === true && this.state.numberOfUnsuccessfulInvitedUser > 0
                    ? this._renderUserResolveActionFailureMessageBar()
                    : null}
                {this.hasTenantConnected === true && this.state.showDisconnectedUserWarning === true ? this._renderUnresolvedUserWarning() : null}
                {this.state.showDownloadErrorMessageBar === true ? this._renderDownloadErrorMessage() : null}
                {this._renderAadConnectContent()}
                {this.state.updateAadInProgress ? (
                    <div className="aad-overlay flex-column flex-center justify-center">
                        <Spinner size={SpinnerSize.large} />
                    </div>
                ) : null}
                {this.state.isDownloadInProgress ? (
                    <div className="aad-overlay flex-column flex-center justify-center">
                        <Spinner
                            size={SpinnerSize.large}
                            label={format(AdminResources.DownloadSpinnerMessage, this.state.data.orgnizationTenantData.displayName)}
                        />
                    </div>
                ) : null}
                {this.state.showAadConnectPanel && this.state.userTenantData ? (
                    <WrappedComponent<IAadConnectPanelComponentProps>
                        dependencies={["ms.vss-admin-web.admin-organization-aad-controls"]}
                        wrappedType={"ms.vss-admin-web.aad-connect-panel"}
                        wrappedProps={{
                            pageContext: this.context.pageContext,
                            orgTenantData: this.state.data.orgnizationTenantData,
                            userData: this.state.data.user,
                            userTenantData: this.state.userTenantData,
                            onDismiss: this._dismissAadConnectPanel,
                            hasTenantConnected: this.hasTenantConnected,
                            onConnect: this._onConnectToAad,
                            organizationId: this.state.data.organizationId,
                            organizationName: this.state.data.organizationName,
                            onUpdateUnresolvedUsers: this._onUpdateUnresolvedUsers,
                            onOpenDisconnectUserDialog: this._onOpenDisconnectUserDialog
                        }}
                    />
                ) : null}
                {this.state.showResolvePanel ? (
                    <WrappedComponent<IAadResolvePanelComponentProps>
                        dependencies={["ms.vss-admin-web.admin-organization-aad-controls"]}
                        wrappedType={"ms.vss-admin-web.aad-resolve-panel"}
                        wrappedProps={{
                            pageContext: this.context.pageContext,
                            orgTenantData: this.state.data.orgnizationTenantData,
                            organizationName: this.state.data.organizationName,
                            disconnectUsers: this.state.disconnectedUsers,
                            isAadGuestUser: this.state.data.user.isGuestUserInAad,
                            onMapIdentities: this._onMapIdentities,
                            onDismiss: this._dismissResolvePanel
                        }}
                    />
                ) : null}
                {this.state.showSendInvitePanel ? (
                    <WrappedComponent<IAadSendInvitePanelComponentProps>
                        dependencies={["ms.vss-admin-web.admin-organization-aad-controls"]}
                        wrappedType={"ms.vss-admin-web.aad-send-invite-panel"}
                        wrappedProps={{
                            pageContext: this.context.pageContext,
                            orgTenantData: this.state.data.orgnizationTenantData,
                            disconnectedUsers: this.state.disconnectedUsers,
                            totalOrgUsers: this.state.totalOrgUsers,
                            numberOfSuccessMappedUsers: this.state.numberOfSuccessfulMappedUser,
                            numberOfUnsuccessMappedUsers: this.state.numberOfUnsuccessfulMappedUser,
                            onSendInvite: this._onSendInvite,
                            onDismiss: this._dismissSendInvitePanel,
                            onOpenFailureMappingUserDialog: this._onOpenFailureMappingUserDialog
                        }}
                    />
                ) : null}
                {this.state.showMappingLoadingPanel ? (
                    <WrappedComponent<IAadResolveLoadingPanelComponentProps>
                        dependencies={["ms.vss-admin-web.admin-organization-aad-controls"]}
                        wrappedType={"ms.vss-admin-web.aad-resolve-loading-panel"}
                        wrappedProps={{
                            loadingMessage: format(
                                AdminResources.MappingLoadingMessage,
                                this.state.mappingUserInProgress,
                                this.state.data.orgnizationTenantData.displayName
                            )
                        }}
                    />
                ) : null}
                {this.state.showInvitationLoadingPanel ? (
                    <WrappedComponent<IAadResolveLoadingPanelComponentProps>
                        dependencies={["ms.vss-admin-web.admin-organization-aad-controls"]}
                        wrappedType={"ms.vss-admin-web.aad-resolve-loading-panel"}
                        wrappedProps={{
                            loadingMessage: format(
                                AdminResources.InviteLoadingMessage,
                                this.state.disconnectedUsers.length,
                                this.state.data.orgnizationTenantData.displayName
                            )
                        }}
                    />
                ) : null}
                {this.state.showAadDisconnectDialog ? (
                    <WrappedComponent<IAadDisconnectDialogComponentProps>
                        dependencies={["ms.vss-admin-web.admin-organization-aad-controls"]}
                        wrappedType={"ms.vss-admin-web.aad-disconnect-dialog"}
                        wrappedProps={{
                            pageContext: this.context.pageContext,
                            orgTenantData: this.state.data.orgnizationTenantData,
                            organizationName: this.state.data.organizationName,
                            onDismiss: this._dismissAadDisconnectPanel,
                            onDisonnect: this._onDisconnectToAad
                        }}
                    />
                ) : null}
                {this.state.showAadDisconnectSuccessDialog ? (
                    <WrappedComponent<IAadActionSuccessDialogComponentProps>
                        dependencies={["ms.vss-admin-web.admin-organization-aad-controls"]}
                        wrappedType={"ms.vss-admin-web.aad-action-success-dialog"}
                        wrappedProps={{
                            orgName: this.state.data.organizationName,
                            isConnectAction: false,
                            onCloseAndSignout: this._onCloseAndSignout,
                            orgTenantName: this.state.data.orgnizationTenantData.displayName,
                            onOpenDisconnectUserDialog: this._onOpenDisconnectUserDialog,
                            showTooManyUsersWarning: this.state.showTooManyUsersWarning
                        }}
                    />
                ) : null}
                {this.state.showAadConnectSuccessDialog ? (
                    <WrappedComponent<IAadActionSuccessDialogComponentProps>
                        dependencies={["ms.vss-admin-web.admin-organization-aad-controls"]}
                        wrappedType={"ms.vss-admin-web.aad-action-success-dialog"}
                        wrappedProps={{
                            orgName: this.state.data.organizationName,
                            isConnectAction: true,
                            onCloseAndSignout: this._onCloseAndSignout,
                            orgTenantName: this.state.newOrgTenantName,
                            onOpenDisconnectUserDialog: this._onOpenDisconnectUserDialog,
                            countOfUnresolvedUsers: this.state.disconnectedUsers.length,
                            showTooManyUsersWarning: this.state.showTooManyUsersWarning
                        }}
                    />
                ) : null}
                {this.state.showAadConnectUnsuccessDialog ? (
                    <WrappedComponent<IAadActionUnsuccessDialogComponentProps>
                        dependencies={["ms.vss-admin-web.admin-organization-aad-controls"]}
                        wrappedType={"ms.vss-admin-web.aad-action-unsuccess-dialog"}
                        wrappedProps={{
                            orgName: this.state.data.organizationName,
                            isConnectAction: true,
                            onClose: this._onCloseUnsuccessAction,
                            error: this.state.aadConnectError,
                            orgTenantName: this.state.newOrgTenantName
                        }}
                    />
                ) : null}
                {this.state.showAadDisconnectUnsuccessDialog ? (
                    <WrappedComponent<IAadActionUnsuccessDialogComponentProps>
                        dependencies={["ms.vss-admin-web.admin-organization-aad-controls"]}
                        wrappedType={"ms.vss-admin-web.aad-action-unsuccess-dialog"}
                        wrappedProps={{
                            orgName: this.state.data.organizationName,
                            isConnectAction: false,
                            onClose: this._onCloseUnsuccessAction,
                            error: this.state.aadDisconnectError,
                            orgTenantName: this.state.data.orgnizationTenantData.displayName
                        }}
                    />
                ) : null}
                {this.state.showDisconnectUserDialog === true ? (
                    <WrappedComponent<IUsersListDialogComponentProps>
                        dependencies={["ms.vss-admin-web.admin-common"]}
                        wrappedType={"ms.vss-admin-web.users-list-dialog"}
                        wrappedProps={{
                            users: this.state.disconnectedUsers,
                            onClose: () => this._onCloseDisconnectUserDialog(),
                            title: AdminResources.DisconnectedUsersDialogTitle,
                            pageContext: this.context.pageContext
                        }}
                    />
                ) : null}
                {this.state.showFailureMappedUsersDialog === true ? (
                    <WrappedComponent<IMultiOperationDialogProps>
                        wrappedType="ms.vss-admin-web.multi-operation-dialog"
                        dependencies={["ms.vss-admin-web.admin-common-controls-content"]}
                        wrappedProps={{
                            title: AdminResources.AadMappingUserErrorDialogTitlte,
                            statusText: AdminResources.AadMappingUserErrorDialogTitlte,
                            primaryColumnHeading: AdminResources.MappedUserColumn,
                            secondaryColumnHeading: AdminResources.IncorrectMappingColumn,
                            onCancel: this._onCloseFailureMappingUsersDialog,
                            operations: this.state.failureMappingUsersCustomModel
                        }}
                    />
                ) : null}
                {this.state.showFailureInvitingUsersDialog === true ? (
                    <WrappedComponent<IUsersListDialogComponentProps>
                        dependencies={["ms.vss-admin-web.admin-common"]}
                        wrappedType={"ms.vss-admin-web.users-list-dialog"}
                        wrappedProps={{
                            users: this.state.disconnectedUsers,
                            onClose: () => this._onCloseFailureInvitingUsersDialog(),
                            title: AdminResources.DisconnectedUsersDialogTitle,
                            pageContext: this.context.pageContext
                        }}
                    />
                ) : null}
                {this.state.showOrgCreationAddAadGroupPanel === true ? (
                    <WrappedComponent<IAddAadGroupPanelComponentProps>
                        dependencies={["ms.vss-admin-web.admin-organization-aad-policy-controls"]}
                        wrappedType={"ms.vss-admin-web.aad-policy-add-group-panel"}
                        wrappedProps={{
                            pageContext: this.context.pageContext,
                            onDismiss: (() => {
                                this.setState({
                                    showOrgCreationAddAadGroupPanel: false
                                });
                            }),
                            onAddAadGroups: (addedIdentities => {
                                this.setState({
                                    showOrgCreationAddAadGroupPanel: false
                                });

                                this._onAddAadGroupsOrUsers(addedIdentities, this.state.orgPolicyData);
                            })
                        }}
                    />
                ) : null}
                {this.state.showGlobalPatAddAadGroupPanel === true ? (
                    <WrappedComponent<IAddAadGroupPanelComponentProps>
                        dependencies={["ms.vss-admin-web.admin-organization-aad-policy-controls"]}
                        wrappedType={"ms.vss-admin-web.aad-policy-add-group-panel"}
                        wrappedProps={{
                            pageContext: this.context.pageContext,
                            onDismiss: (() => {
                                this.setState({
                                    showGlobalPatAddAadGroupPanel: false,
                                    currentPolicyData: undefined
                                });
                            }),
                            onAddAadGroups: (addedIdentities => {
                                this._onAddAadGroupsOrUsers(addedIdentities, this.state.currentPolicyData);

                                this.setState({
                                    showGlobalPatAddAadGroupPanel: false,
                                    currentPolicyData: undefined
                                });
                            })
                        }}
                    />
                ) : null}
                {this.state.showEditErrorMessagePanel === true ? (
                    <WrappedComponent<IEditDisplayMessagePanelComponentProps>
                        dependencies={["ms.vss-admin-web.admin-organization-aad-policy-controls"]}
                        wrappedType={"ms.vss-admin-web.aad-policy-error-message-panel"}
                        wrappedProps={{
                            pageContext: this.context.pageContext,
                            displayErrorMessage:
                                this.state.orgPolicyData &&
                                    this.state.orgPolicyData.properties &&
                                    this.state.orgPolicyData.properties[PolicyProperties.ErrorMessage]
                                    ? this.state.orgPolicyData.properties[PolicyProperties.ErrorMessage]
                                    : AdminResources.DefaultOrgCreationErrorMessage,
                            onDismiss: (() => {
                                this.setState({
                                    showEditErrorMessagePanel: false
                                });
                            }),
                            onUpdateErrorMessage: this._onUpdateErrorMessage
                        }}
                    />
                ) : null}
                {this.state.showFullScopePatAddAadGroupPanel === true ? (
                    <WrappedComponent<IAddAadGroupPanelComponentProps>
                        dependencies={["ms.vss-admin-web.admin-organization-aad-policy-controls"]}
                        wrappedType={"ms.vss-admin-web.aad-policy-add-group-panel"}
                        wrappedProps={{
                            pageContext: this.context.pageContext,
                            onDismiss: (() => {
                                this.setState({
                                    showFullScopePatAddAadGroupPanel: false,
                                    currentPolicyData: undefined
                                });
                            }),
                            onAddAadGroups: (addedIdentities => {
                                this._onAddAadGroupsOrUsers(addedIdentities, this.state.currentPolicyData);

                                this.setState({
                                    showFullScopePatAddAadGroupPanel: false,
                                    currentPolicyData: undefined
                                });
                            })
                        }}
                    />
                ) : null}
                {this.state.showTokenLifespanAddAadGroupPanel === true ? (
                    <WrappedComponent<IAddAadGroupPanelComponentProps>
                        dependencies={["ms.vss-admin-web.admin-organization-aad-policy-controls"]}
                        wrappedType={"ms.vss-admin-web.aad-policy-add-group-panel"}
                        wrappedProps={{
                            pageContext: this.context.pageContext,
                            onDismiss: (() => {
                                this.setState({
                                    showTokenLifespanAddAadGroupPanel: false,
                                    currentPolicyData: undefined
                                });
                            }),
                            onAddAadGroups: (addedIdentities => {
                                this._onAddAadGroupsOrUsers(addedIdentities, this.state.currentPolicyData);

                                this.setState({
                                    showTokenLifespanAddAadGroupPanel: false,
                                    currentPolicyData: undefined
                                });
                            })
                        }}
                    />
                ) : null}
            </div>
        );
    }

    private _renderDownloadErrorMessage(): JSX.Element {
        return (
            <div className="width-100 message-bar-div">
                <MessageBar severity={MessageBarSeverity.Error}>
                    <span className="text-word-break">{this.state.downloadErrorMessage}</span>
                </MessageBar>
            </div>
        );
    }

    private _renderNoUserTenantInfoMessage(): JSX.Element {
        return this.state.userTenantData ? (
            <div className="width-100 message-bar-div no-user-tenant-div">
                <MessageBar severity={MessageBarSeverity.Info}>
                    <span className="text-word-break">{AdminResources.AadMainPageUserNoTenant}</span>
                </MessageBar>
            </div>
        ) : (
                <Spinner label={AdminResources.FetchingTenants} className="flex-grow justify-center">
                    {AdminResources.FetchingTenants}
                </Spinner>
            );
    }

    private _renderUserResolveActionSuccessMessageBar(): JSX.Element {
        if (this.state.numberOfSuccessfulInvitedUser > 0 && this.state.numberOfSuccessfulMappedUser > 0) {
            return (
                <div className="width-100 message-bar-div user-resolve-action-success-div">
                    <MessageBar severity={MessageBarSeverity.Success}>
                        <span className="text-word-break">
                            {format(
                                AdminResources.UserResolveActionSuccessMessage,
                                this.state.numberOfSuccessfulMappedUser,
                                this.state.numberOfSuccessfulInvitedUser,
                                this.state.data.orgnizationTenantData.displayName
                            )}
                        </span>
                    </MessageBar>
                </div>
            );
        } else if (this.state.numberOfSuccessfulMappedUser > 0) {
            return (
                <div className="width-100 message-bar-div user-resolve-action-success-div">
                    <MessageBar severity={MessageBarSeverity.Success}>
                        <span className="text-word-break">
                            {format(AdminResources.UserMappingSuccessMessage, this.state.numberOfSuccessfulMappedUser)}
                        </span>
                    </MessageBar>
                </div>
            );
        } else {
            return (
                <div className="width-100 message-bar-div user-resolve-action-success-div">
                    <MessageBar severity={MessageBarSeverity.Success}>
                        <span className="text-word-break">
                            {format(
                                AdminResources.UserInviteSuccessMessage,
                                this.state.numberOfSuccessfulInvitedUser,
                                this.state.data.orgnizationTenantData.displayName
                            )}
                        </span>
                    </MessageBar>
                </div>
            );
        }
    }

    private _renderUserResolveActionFailureMessageBar(): JSX.Element {
        return (
            <div className="user-action-failure-div width-100 message-bar-div">
                <MessageBar
                    severity={MessageBarSeverity.Warning}
                    buttonProps={[
                        {
                            className: "primary-action-button unresolve-message-button",
                            onClick: this._openResolvePanel,
                            primary: true,
                            text: AdminResources.Resolve
                        }
                    ]}
                >
                    <div className="block-display">
                        <FormatComponent format={AdminResources.UserInviteActionFailureMessage}>
                            <Link className="cursor-pointer" onClick={this._onOpenFailureInvitingUsersDialog}>
                                <span
                                    className="text-word-break"
                                    dangerouslySetInnerHTML={createMarkup(
                                        format(AdminResources.UserInviteActionFailureLink, this.state.numberOfUnsuccessfulInvitedUser)
                                    )}
                                />
                            </Link>
                            <Link href={AdminResources.AadConnectLearnMoreUrl} target="_blank">
                                {AdminResources.UserResolveReviewDocumentation}
                            </Link>
                        </FormatComponent>
                    </div>
                </MessageBar>
            </div>
        );
    }

    private _renderUnresolvedUserWarning(): JSX.Element {
        return (
            <div className="top-margin unresolve-message width-100 message-bar-div">
                <MessageBar
                    severity={MessageBarSeverity.Warning}
                    buttonProps={[
                        {
                            className: "primary-action-button unresolve-message-button",
                            onClick: this._openResolvePanel,
                            primary: true,
                            text: AdminResources.Resolve
                        }
                    ]}
                >
                    <div className="block-display">
                        <span>
                            <Link className="cursor-pointer" onClick={this._onOpenDisconnectUserDialog}>
                                <span
                                    className="text-word-break"
                                    dangerouslySetInnerHTML={createMarkup(
                                        format(AdminResources.AadConnectSuccessLink, this.state.disconnectedUsers.length)
                                    )}
                                />
                            </Link>
                        </span>
                        <span
                            className="text-word-break"
                            dangerouslySetInnerHTML={createMarkup(
                                format(
                                    AdminResources.AadConnectedBannerDescription,
                                    filterXSS(this.state.data.organizationName),
                                    filterXSS(this.state.data.orgnizationTenantData.displayName)
                                )
                            )}
                        />
                        <span
                            className="text-word-break"
                            dangerouslySetInnerHTML={createMarkup(
                                format(
                                    AdminResources.AadConnectedBannerUnableToLogin,
                                    filterXSS(this.state.data.orgnizationTenantData.displayName)
                                )
                            )}
                        />
                        {<span className="text-word-break" dangerouslySetInnerHTML={createMarkup(AdminResources.AadConnectedDeleteUsers)} />}
                    </div>
                </MessageBar>
            </div>
        );
    }

    private _renderAadConnectContent(): JSX.Element {
        if (this.hasTenantConnected === false) {
            return (
                <div className="top-margin bottom-margin no-org-tenant-connect-div">
                    <div className="fontSizeM overview-heading">{AdminResources.AadConnectDescription}</div>
                    <div className="top-margin">
                        <Link className="cursor-pointer" href={AdminResources.AadConnectLearnMoreUrl} target="_blank">
                            {AdminResources.AadConnectLearnMore}
                        </Link>
                    </div>
                    <div className="top-margin">
                        <Button
                            text={AdminResources.AadConnectButton}
                            onClick={() => {
                                this._openAadConnectPanel(false);
                            }}
                            disabled={!this.state.data.hasModifyPermissions || !this.state.hasUserTenant}
                            primary
                        />
                    </div>
                </div>
            );
        } else {
            return (
                <div className="top-margin bottom-margin org-tenant-connect-div">
                    <div className="fontSizeM overview-heading">
                        <span
                            dangerouslySetInnerHTML={createMarkup(
                                format(
                                    AdminResources.AadConnectedDescription,
                                    filterXSS(this.state.data.orgnizationTenantData.displayName)
                                )
                            )}
                        />
                    </div>
                    <div className="flex-row flex-center small-top-margin tenant-aad-info-div">
                        <div className="aad-logo-div">
                            <Icon iconName="AzureLogo" className="aad-icon" />
                        </div>
                        <div>
                            <div className="small-top-margin fontWeightSemiBold">{this.state.data.orgnizationTenantData.displayName}</div>
                            <div className="small-top-margin">{this.state.data.orgnizationTenantData.domain}</div>
                            <div className="small-top-margin">{format(AdminResources.TenantId, this.state.data.orgnizationTenantData.id)}</div>
                        </div>
                    </div>
                    {!this.isSwitchTenantEnabled ? (
                        <div className="top-margin">
                            <FormatComponent format={AdminResources.AadConnectedLearnHowTo}>
                                <Link className="cursor-pointer" href={AdminResources.AadConnectedLearnHowToSwitchTenantLink} target="_blank">
                                    {AdminResources.AadConnectedLearnHowToSwitchTenant}
                                </Link>
                            </FormatComponent>
                        </div>
                    ) : null}
                    <div className="top-margin">
                        <FormatComponent format={AdminResources.AadConnectedLearnCheckOut}>
                            <Link className="cursor-pointer" href={AdminResources.AadConnectedLearnHowToFaqLink} target="_blank">
                                {AdminResources.AadConnectedLearnFaq}
                            </Link>
                        </FormatComponent>
                    </div>
                    <div className="top-margin">
                        <Button
                            text={AdminResources.AadDisconnectButton}
                            onClick={this._openAadDisconnectPanel}
                            disabled={!this.state.data.hasModifyPermissions}
                            primary
                        />
                        {this.state.userTenantData && this.state.userTenantData.userTenants.length > 1 && this.isSwitchTenantEnabled ? (
                            <Button
                                className="small-left-margin"
                                text={AdminResources.AadSwitchButton}
                                onClick={() => {
                                    this._openAadConnectPanel(true);
                                }}
                                disabled={!this.state.data.hasModifyPermissions}
                            />
                        ) : null}
                    </div>

                    {!this.state.data.isMicrosoftTenant ? this._renderDownloadConnectedOrgs() : null}

                    {this.isOrgPolicyEnabled === true && this.state.orgPolicyData && this._renderOrgPolicyContent()}
                    {this.isGlobalPatPolicyEnabled === true && this.state.globalPatPolicyData && this._renderGlobalPatPolicyContent()}
                    {this.isFullScopePatPolicyEnabled === true && this.state.fullScopePatPolicyData && this._renderFullScopePatPolicyContent()}
                    {this.isTokenLifespanPolicyEnabled === true && this.state.tokenLifespanPolicyData && this._renderTokenLifespanPolicyContent()}                                        
                    {this.isTokenAutoRevocationPolicyEnabled === true && this.state.tokenAutoRevocationPolicyData && this._renderTokenAutoRevocationPolicyContent()}
                </div>
            );
        }
    }

    private _renderPolicyNotice(): JSX.Element {
        return (
            <div className="grey-font">
                <FormatComponent format={AdminResources.PolicyAllowListDescriptionNotice}>
                    <Link className="cursor-pointer" href={AdminResources.PolicyAllowListDescriptionNoticeLink} target="_blank">
                        {AdminResources.PolicyAllowListDescriptionNoticeHere}
                    </Link>
                </FormatComponent>
            </div>
        );
    }

    private _renderOrgPolicyContent(): JSX.Element {
        return (
            <div className="org-policy-content-div">
                <div className="overview-divider" />
                <div className="big-top-margin fontSizeL fontWeightSemiBold">{AdminResources.AadPoliciesTitle}</div>
                {this.state.showOrgCreationPolicyErrorMessageBar === true && (
                    <div className="width-100 message-bar-div small-top-margin">
                        <MessageBar severity={MessageBarSeverity.Error}>
                            <span className="text-word-break">{this.state.orgCreationPolicyErrorMessage}</span>
                        </MessageBar>
                    </div>
                )}
                <div className="top-margin tenant-policy-div">
                    <div className="tenant-policy-title fontWeightSemiBold flex-row flex-center flex-align-start">
                        <div>{AdminResources.OrgCreationPolicyTitle}</div>
                        <div className="tenant-policy-toggle">
                            <Toggle checked={this.orgPolicyToggleEnabled}
                                onChange={(env: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, isChecked: boolean) => this._onPolicyToggleChange(this.state.orgPolicyData, env, isChecked)}
                                ariaLabel={AdminResources.OrgCreationPolicyTitle}/>
                        </div>
                        {this.state.isOrgPolicyUpdateInProgress === true && (
                            <div className="update-tenant-policy-spinner">
                                <Spinner size={SpinnerSize.small} />
                            </div>
                        )}
                    </div>
                    <div className="grey-font">
                        {format(AdminResources.OrgCreationPolicyDescription, this.state.data.orgnizationTenantData.displayName)}
                    </div>

                    <div className="top-margin">
                        <div className="fontSizeM fontWeightSemiBold">{AdminResources.OrgCreationPolicyAllowList}</div>
                        <div className="grey-font">{AdminResources.OrgCreationPolicyAllowListDescription}</div>
                        {this._renderPolicyNotice()}
                    </div>
                    {this.state.orgPolicyData &&
                        this.state.orgPolicyData.properties &&
                        this.state.orgPolicyData.properties[PolicyProperties.AllowedUsersAndGroupObjectIds] &&
                        this._renderAadGroups(this.state.orgPolicyData, this.orgPolicyToggleEnabled)}
                    <div className="top-margin">
                        <Button
                            text={AdminResources.OrgCreationPolicyAddGroupButton}
                            onClick={() => {
                                this._startActionCapture("Show Org CreationAdd AAD Group Panel", {});
                                this.setState({
                                    showOrgCreationAddAadGroupPanel: true
                                });
                            }}
                            disabled={!this.orgPolicyToggleEnabled.value}
                            primary
                        />
                    </div>
                    <div className="top-margin">
                        <div className="fontSizeM fontWeightSemiBold">{AdminResources.OrgCreationPolicyErrorMessageTitle}</div>
                        <div className="grey-font org-policy-display-error-message">
                            {this.state.orgPolicyData &&
                                this.state.orgPolicyData.properties &&
                                this.state.orgPolicyData.properties[PolicyProperties.ErrorMessage]
                                ? this.state.orgPolicyData.properties[PolicyProperties.ErrorMessage]
                                : AdminResources.DefaultOrgCreationErrorMessage}
                        </div>
                    </div>
                    <div className="top-margin">
                        <Button
                            text={AdminResources.OrgCreationPolicyErrorMessageButton}
                            onClick={() => {
                                this._startActionCapture("Show Edit Error Message Panel", {});
                                this.setState({
                                    showEditErrorMessagePanel: true
                                });
                            }}
                            disabled={!this.orgPolicyToggleEnabled.value}
                            primary
                        />
                    </div>
                </div>
            </div>
        );
    }

    private _renderGlobalPatPolicyContent(): JSX.Element {
        return (
            <div className="global-pat-policy-content-div">
                <div className="overview-divider" />
                {this.state.showGlobalPatPolicyErrorMessageBar === true && (
                    <div className="width-100 message-bar-div small-top-margin">
                        <MessageBar severity={MessageBarSeverity.Error}>
                            <span className="text-word-break">{this.state.globalPatPolicyErrorMessage}</span>
                        </MessageBar>
                    </div>
                )}
                <div className="top-margin tenant-policy-div">
                    <div className="tenant-policy-title fontWeightSemiBold flex-row flex-center flex-align-start">
                        <div>{AdminResources.GlobalPatCreationPolicyTitle}</div>
                        <div className="tenant-policy-toggle">
                            <Toggle checked={this.globalPatPolicyToggleEnabled}
                                onChange={(env: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, isChecked: boolean) => this._onPolicyToggleChange(this.state.globalPatPolicyData, env, isChecked)}
                                ariaLabel={AdminResources.GlobalPatCreationPolicyTitle}/>
                        </div>
                        {this.state.isGlobalPatPolicyUpdateInProgress === true && (
                            <div className="update-tenant-policy-spinner">
                                <Spinner size={SpinnerSize.small} />
                            </div>
                        )}
                    </div>
                    <div className="grey-font">
                        <div>{AdminResources.GlobalPatCreationPolicyDescription}</div>
                    </div>

                    <div className="top-margin">
                        <div className="fontSizeM fontWeightSemiBold">{AdminResources.PatCreationPolicyAllowList}</div>
                        <div className="grey-font">{format(AdminResources.PatCreationPolicyAllowListDescription, AdminResources.GlobalPat, "")}</div>
                        {this._renderPolicyNotice()}
                    </div>
                    {this.state.globalPatPolicyData &&
                        this.state.globalPatPolicyData.properties &&
                        this.state.globalPatPolicyData.properties[PolicyProperties.AllowedUsersAndGroupObjectIds] &&
                        this._renderAadGroups(this.state.globalPatPolicyData, this.globalPatPolicyToggleEnabled)}
                    <div className="top-margin">
                        <Button
                            text={AdminResources.PatCreationPolicyAddGroupButton}
                            onClick={() => {
                                this._startActionCapture("Show Global PAT Add AAD Group Panel", {});
                                this.setState({
                                    showGlobalPatAddAadGroupPanel: true,
                                    currentPolicyData: this.state.globalPatPolicyData
                                });
                            }}
                            disabled={!this.globalPatPolicyToggleEnabled.value}
                            primary
                        />
                    </div>
                </div>
            </div>
        );
    }

    private _renderFullScopePatPolicyContent(): JSX.Element {
        return (
            <div className="full-scope-pat-policy-content-div">
                <div className="overview-divider" />
                {this.state.showFullScopePatPolicyErrorMessageBar === true && (
                    <div className="width-100 message-bar-div small-top-margin">
                        <MessageBar severity={MessageBarSeverity.Error}>
                            <span className="text-word-break">{this.state.fullScopePatPolicyErrorMessage}</span>
                        </MessageBar>
                    </div>
                )}
                <div className="top-margin tenant-policy-div">
                    <div className="tenant-policy-title fontWeightSemiBold flex-row flex-center flex-align-start">
                        <div>{AdminResources.FullScopePatCreationPolicyTitle}</div>
                        <div className="tenant-policy-toggle">
                            <Toggle checked={this.fullScopePatPolicyToggleEnabled}
                                onChange={(env: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, isChecked: boolean) => this._onPolicyToggleChange(this.state.fullScopePatPolicyData, env, isChecked)}
                                ariaLabel={AdminResources.FullScopePatCreationPolicyTitle}/>
                        </div>
                        {this.state.isFullScopePatPolicyUpdateInProgress === true && (
                            <div className="update-tenant-policy-spinner">
                                <Spinner size={SpinnerSize.small} />
                            </div>
                        )}
                    </div>
                    <div className="grey-font">
                        <div>{AdminResources.FullScopePatCreationPolicyDescription}</div>
                    </div>
                    
                    <div className="top-margin">
                        <div className="fontSizeM fontWeightSemiBold">{AdminResources.PatCreationPolicyAllowList}</div>
                        <div className="grey-font">{format(AdminResources.PatCreationPolicyAllowListDescription, AdminResources.FullScopedPat, "")}</div>
                        {this._renderPolicyNotice()}
                    </div>
                    {this.state.fullScopePatPolicyData &&
                        this.state.fullScopePatPolicyData.properties &&
                        this.state.fullScopePatPolicyData.properties[PolicyProperties.AllowedUsersAndGroupObjectIds] &&
                        this._renderAadGroups(this.state.fullScopePatPolicyData, this.fullScopePatPolicyToggleEnabled)}
                    <div className="top-margin">
                        <Button
                            text={AdminResources.PatCreationPolicyAddGroupButton}
                            onClick={() => {
                                this._startActionCapture("Show Full-scoped PAT Add AAD Group Panel", {});
                                this.setState({
                                    showFullScopePatAddAadGroupPanel: true,
                                    currentPolicyData: this.state.fullScopePatPolicyData
                                });
                            }}
                            disabled={!this.fullScopePatPolicyToggleEnabled.value}
                            primary
                        />
                    </div>
                </div>
            </div>
        );
    }

    private _renderTokenLifespanPolicyContent(): JSX.Element {
        return (
            <div className="token-lifespan-policy-content-div">
                <div className="overview-divider" />
                {this.state.showTokenLifespanPolicyErrorMessageBar === true && (
                    <div className="width-100 message-bar-div small-top-margin">
                        <MessageBar severity={MessageBarSeverity.Error}>
                            <span className="text-word-break">{this.state.tokenLifespanPolicyErrorMessage}</span>
                        </MessageBar>
                    </div>
                )}
                <div className="top-margin tenant-policy-div">
                    <div className="tenant-policy-title fontWeightSemiBold flex-row flex-center flex-align-start">
                        <div>{AdminResources.EnforceTokenLifespanPolicyTitle}</div>
                        <div className="tenant-policy-toggle">
                            <Toggle checked={this.tokenLifespanPolicyToggleEnabled}
                                onChange={(env: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, isChecked: boolean) => this._onPolicyToggleChange(this.state.tokenLifespanPolicyData, env, isChecked)}
                                ariaLabel={AdminResources.EnforceTokenLifespanPolicyTitle}/>
                        </div>
                        {this.state.isTokenLifespanPolicyUpdateInProgress === true && (
                            <div className="update-tenant-policy-spinner">
                                <Spinner size={SpinnerSize.small} />
                            </div>
                        )}
                    </div>
                    <div className="grey-font">
                        <div>{AdminResources.EnforceTokenLifespanPolicyDescription}</div>
                    </div>

                    <div className="top-margin">
                        <div className="fontSizeM fontWeightSemiBold">{AdminResources.EnforceTokenLifespanPolicyMaxLifespanDays}</div>
                    </div>

                    <div className="top-margin tenant-policy-text-input">
                        <TextField className="tenant-policy-text-box"
                            onChange={(a, b) => this._onUpdatePendingMaxLifespanDays(a, b, this.state.tokenLifespanPolicyData)}
                            disabled={!this.tokenLifespanPolicyToggleEnabled.value}
                            value={this.state.tokenLifespanPendingMaxLifespanDays.toString()}
                            ariaLabel={AdminResources.EnforceTokenLifespanPolicyMaxLifespanDays}
                        />

                        <Button
                            text={AdminResources.EnforceTokenLifespanPolicyMaxLifespanDaysButton}
                            onClick={() => this._onSaveMaxLifespanDays(this.state.tokenLifespanPolicyData)}
                            disabled={!this.tokenLifespanPolicyToggleEnabled.value || this.state.showTokenLifespanPolicyErrorMessageBar}
                            primary
                        />
                    </div>
                    
                    <div className="top-margin">
                        <div className="fontSizeM fontWeightSemiBold">{AdminResources.PatCreationPolicyAllowList}</div>
                        <div className="grey-font">{format(AdminResources.PatCreationPolicyAllowListDescription, "", AdminResources.PatLifespan)}</div>
                        {this._renderPolicyNotice()}
                    </div>
                    {this.state.tokenLifespanPolicyData &&
                        this.state.tokenLifespanPolicyData.properties &&
                        this.state.tokenLifespanPolicyData.properties[PolicyProperties.AllowedUsersAndGroupObjectIds] &&
                        this._renderAadGroups(this.state.tokenLifespanPolicyData, this.tokenLifespanPolicyToggleEnabled)}
                    <div className="top-margin">
                        <Button
                            text={AdminResources.PatCreationPolicyAddGroupButton}
                            onClick={() => {
                                this._startActionCapture("Show PAT lifespan Add AAD Group Panel", {});
                                this.setState({
                                    showTokenLifespanAddAadGroupPanel: true,
                                    currentPolicyData: this.state.tokenLifespanPolicyData
                                });
                            }}
                            disabled={!this.tokenLifespanPolicyToggleEnabled.value}
                            primary
                        />
                    </div>
                </div>
            </div>
        );
    }

    private _renderTokenAutoRevocationPolicyContent(): JSX.Element{
        return (
            <div className="token-auto-revocation-policy-content-div">
                <div className="overview-divider" />
                {this.state.showTokenAutoRevocationPolicyErrorMessageBar === true && (
                    <div className="width-100 message-bar-div small-top-margin">
                        <MessageBar severity={MessageBarSeverity.Error}>
                            <span className="text-word-break">{this.state.tokenAutoRevocationPolicyErrorMessage}</span>
                        </MessageBar>
                    </div>
                )}
                <div className="top-margin tenant-policy-div">
                    <div className="tenant-policy-title fontWeightSemiBold flex-row flex-center flex-align-start">
                        <div>{AdminResources.EnableTokenAutoRevocationPolicyTitle}</div>
                        <div className="tenant-policy-toggle">
                            <Toggle checked={this.tokenAutoRevocationPolicyToggleEnabled.value}
                                onChange={(env: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, isChecked: boolean) => this._onPolicyToggleChange(this.state.tokenAutoRevocationPolicyData, env, isChecked)}
                                ariaLabel={AdminResources.EnableTokenAutoRevocationPolicyTitle}/>
                        </div>
                        {this.state.isTokenAutoRevocationPolicyUpdateInProgress === true && (
                            <div className="update-tenant-policy-spinner">
                                <Spinner size={SpinnerSize.small} />
                            </div>
                        )}
                    </div>
                    <div className="grey-font">                       
                        <FormatComponent format={AdminResources.EnableTokenAutoRevocationPolicyDescription}>
                            <Link className="cursor-pointer" href={AdminResources.EnableTokenAutoRevocationPolicyDescriptionLink} target="_blank">
                                {AdminResources.EnableTokenAutoRevocationPolicyDescriptionLearnMore}
                            </Link>
                        </FormatComponent>
                    </div>
                </div>
            </div>
        );
    }

    private _renderDownloadConnectedOrgs(): JSX.Element {
        return (
            <div className="download-connect-org-div">
                <div className="overview-divider" />
                <div className="big-top-margin">
                    <span
                        className="fontSizeM overview-heading"
                        dangerouslySetInnerHTML={createMarkup(
                            format(
                                AdminResources.DownloadConnectedOrgs,
                                filterXSS(this.state.data.orgnizationTenantData.displayName)
                            )
                        )}
                    />
                    <div className="top-margin">
                        <Button
                            text={AdminResources.DownloadButton}
                            onClick={this._downloadConnectedOrgs}
                            disabled={this.state.isDownloadInProgress}
                            primary
                        />
                    </div>
                </div>
            </div>
        );
    }

    private _renderAadGroups(policyData: IAadTenantPolicy, policyToggleEnabled: ObservableValue<boolean>): JSX.Element {
        return (
            <div className="small-top-margin allow-user-group-div">
                <PillGroup className="flex-row">
                    {policyData &&
                        policyData.properties &&
                        policyData.properties[PolicyProperties.AllowedUsersAndGroupObjectIds].map((label: IAadObject, index: number) => (
                            <AadGroupOrUserPillComponent
                                orgPolicyToggleEnabled={policyToggleEnabled.value}
                                identity={label}
                                index={index}
                                onRemove={(a) => this._onRemoveAadGroupOrUser(a, policyData)}
                            />
                        ))}
                </PillGroup>
            </div>
        );
    }



    private _onPolicyToggleChange = (policyData?: IAadTenantPolicy, ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, isChecked?: boolean) => {
        var updatedPolicyData: IAadTenantPolicy | undefined = JSON.parse(JSON.stringify(policyData));
        if (updatedPolicyData) {
            updatedPolicyData.isPolicyEnabled = isChecked ? isChecked : false;

            var policyName = "";
            if (this._isOrgCreationRestrictionPolicy(updatedPolicyData)) {
                policyName = PolicyNames.OrganizationCreationRestriction;
            } else if (this._isGlobalPatCreationRestrictionPolicy(updatedPolicyData)) {
                policyName = PolicyNames.RestrictGlobalPersonalAccessToken;
            } else if (this._isFullScopePatCreationRestrictionPolicy(updatedPolicyData)) {
                policyName = PolicyNames.RestrictFullScopePersonalAccessToken;
            } else if (this._isEnforcePersonalTokenLifespanPolicy(updatedPolicyData)) {
                policyName = PolicyNames.RestrictPersonalAccessTokenLifespan;
            } else if(this. _isEnableLeakedPersonalAccessTokenAutoRevocationPolicy(updatedPolicyData)){
                policyName = PolicyNames.EnableLeakedPersonalAccessTokenAutoRevocation;
            }            

            this.updatePolicyData(updatedPolicyData, isChecked ? "Enable " + policyName : "Disable " + policyName, {});
        }
    };

    private _startActionCapture = (action: string, properties: { [key: string]: any }): AdminFeatureTelemetryLogger => {
        return this.telemetryLogger.startActionCapture(Feature.OrganizationAad, Feature.OrganizationAad, action, properties);
    };

    private _dismissAadConnectPanel = () => {
        this.setState({ showAadConnectPanel: false });
    };

    private _openAadConnectPanel = (isSwitch?: boolean) => {
        this._startActionCapture("Show AAD Connect Panel", { IsSwitch: isSwitch });
        this.setState({ showAadConnectPanel: true });
    };

    private _openResolvePanel = () => {
        this._startActionCapture("Show Resolve Panel", {});
        this.setState({ showResolvePanel: true });
    };

    private _dismissResolvePanel = () => {
        this.setState({
            showResolvePanel: false,
            userResolveActionDone: true,
            numberOfSuccessfulMappedUser: 0,
            numberOfUnsuccessfulMappedUser: 0,
            failureMappingUsers: [],
            numberOfSuccessfulInvitedUser: 0,
            numberOfUnsuccessfulInvitedUser: 0,
            showDisconnectedUserWarning: this.state.disconnectedUsers.length > 0 ? true : false
        });
    };

    private _onRemoveAadGroupOrUser = (objectId: string, policyData: IAadTenantPolicy) => {
        var updatedPolicyData: IAadTenantPolicy | undefined = JSON.parse(JSON.stringify(policyData));
        if (updatedPolicyData &&
            updatedPolicyData.properties &&
            (this._isOrgCreationRestrictionPolicy(updatedPolicyData) || 
             this._isGlobalPatCreationRestrictionPolicy(updatedPolicyData) ||
             this._isFullScopePatCreationRestrictionPolicy(updatedPolicyData) ||
             this._isEnforcePersonalTokenLifespanPolicy(updatedPolicyData))) {
            var index = updatedPolicyData.properties[PolicyProperties.AllowedUsersAndGroupObjectIds].findIndex((elem: { objectId: string; }) => elem.objectId === objectId);
            var isUserRemoved = updatedPolicyData.properties[PolicyProperties.AllowedUsersAndGroupObjectIds][index].isUser;
            updatedPolicyData.properties[PolicyProperties.AllowedUsersAndGroupObjectIds].splice(index, 1);
            this.updatePolicyData(updatedPolicyData, "Remove Aad user or group", { IsUserRemoved: isUserRemoved, IsGroupRemoved: !isUserRemoved });
        }
    };


    private _onAddAadGroupsOrUsers = (addedIdentities: IIdentity[], policyData?: IAadTenantPolicy) => {
        if (policyData !== undefined) {
            var updatedPolicyData: IAadTenantPolicy | undefined = JSON.parse(JSON.stringify(policyData));
        } else {
            var updatedPolicyData: IAadTenantPolicy | undefined = JSON.parse(JSON.stringify(this.state.orgPolicyData));
        }

        if (updatedPolicyData &&
            updatedPolicyData.properties &&
            (this._isOrgCreationRestrictionPolicy(updatedPolicyData) || 
                this._isGlobalPatCreationRestrictionPolicy(updatedPolicyData) ||
                this._isFullScopePatCreationRestrictionPolicy(updatedPolicyData) ||
                this._isEnforcePersonalTokenLifespanPolicy(updatedPolicyData))) {
            if (!updatedPolicyData.properties[PolicyProperties.AllowedUsersAndGroupObjectIds]) {
                updatedPolicyData.properties[PolicyProperties.AllowedUsersAndGroupObjectIds] = [];
            }
            var numberOfUsersAdded: number = 0;
            addedIdentities.forEach(identity => {
                if (updatedPolicyData &&
                    updatedPolicyData.properties &&
                    !updatedPolicyData.properties[PolicyProperties.AllowedUsersAndGroupObjectIds].
                        find((userOrGroup: { objectId: string; }) => userOrGroup.objectId === identity.originId)) {
                    var isUser = localeIgnoreCaseComparer(identity.entityType, "user") === 0;
                    updatedPolicyData.properties[PolicyProperties.AllowedUsersAndGroupObjectIds].push({
                        objectId: identity.originId,
                        displayName: identity.displayName ? identity.displayName : "",
                        isUser: isUser
                    });
                    if (isUser) {
                        numberOfUsersAdded++;
                    }
                }
            });

            this.updatePolicyData(updatedPolicyData, "Add AAD user or group", {
                NumberOfUsersAdded: numberOfUsersAdded,
                NumberOfGroupsAdded: addedIdentities.length - numberOfUsersAdded
            });
        }
    };

    private _onUpdatePendingMaxLifespanDays = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, newMaxLifespanDays: string, policyData?: IAadTenantPolicy) => {
        var updatedPolicyData: IAadTenantPolicy | undefined = JSON.parse(JSON.stringify(policyData));
        if (updatedPolicyData && this._isEnforcePersonalTokenLifespanPolicy(updatedPolicyData)) {
            let updatedMaxLifespanDays = Number(newMaxLifespanDays);
            if (updatedMaxLifespanDays != NaN && Number.isInteger(updatedMaxLifespanDays) && updatedMaxLifespanDays > 0 && updatedMaxLifespanDays <= 365) {
                this.setState({
                    showTokenLifespanPolicyErrorMessageBar: false,
                    tokenLifespanPolicyErrorMessage: "",
                    tokenLifespanPendingMaxLifespanDays: newMaxLifespanDays
                });
            } else {
                this.setState({
                    showTokenLifespanPolicyErrorMessageBar: true,
                    tokenLifespanPolicyErrorMessage: "Invalid token lifespan",
                    tokenLifespanPendingMaxLifespanDays: newMaxLifespanDays
                });
            }
        }
    }

    private _onSaveMaxLifespanDays = (policyData?: IAadTenantPolicy) => {
        // Text box input has already been validated, so go ahead and update policy data
        var updatedPolicyData: IAadTenantPolicy | undefined = JSON.parse(JSON.stringify(policyData));
        if (updatedPolicyData &&
            updatedPolicyData.properties &&
            this._isEnforcePersonalTokenLifespanPolicy(updatedPolicyData)) {
            updatedPolicyData.properties[PolicyProperties.MaxPatLifespanInDays] = this.state.tokenLifespanPendingMaxLifespanDays;
            this.updatePolicyData(updatedPolicyData, "Update MaxLifespanDays", {});
        }
    }

    private _onUpdateErrorMessage = (newMessage: string, policyData?: IAadTenantPolicy) => {
        this.setState({
            showEditErrorMessagePanel: false
        });
        //deep copy of an object so that when modifying it, it doesn't modify the original object
        if (policyData !== undefined) {
            var updatedPolicyData: IAadTenantPolicy | undefined = JSON.parse(JSON.stringify(policyData));
        } else {
            var updatedPolicyData: IAadTenantPolicy | undefined = JSON.parse(JSON.stringify(this.state.orgPolicyData));
        }

        if (updatedPolicyData && updatedPolicyData.properties) {
            updatedPolicyData.properties[PolicyProperties.ErrorMessage] = newMessage;

            this.updatePolicyData(updatedPolicyData, "Update error message", {});
        }
    };

    private updatePolicyData = async (policyData: IAadTenantPolicy, telemetryAction: string, properties: { [key: string]: any }) => {
        var source = new OrganizationAadSource();
        properties["TenantId"] = this.state.data.orgnizationTenantData.id;
        var actionTelemetryLogger = this._startActionCapture(telemetryAction, properties);

        if (this._isOrgCreationRestrictionPolicy(policyData)) {
            this.setState({ isOrgPolicyUpdateInProgress: true });
            await source.updateOrgCreationPolicy(this.context.pageContext, policyData).then(
                result => {
                    this.orgPolicyToggleEnabled.value = policyData.isPolicyEnabled === true;
                    this.setState({
                        orgPolicyData: policyData,
                        isOrgPolicyUpdateInProgress: false,
                        showOrgCreationPolicyErrorMessageBar: false,
                        orgCreationPolicyErrorMessage: ""
                    });
                },
                reason => {
                    this.setState({
                        isOrgPolicyUpdateInProgress: false,
                        showOrgCreationPolicyErrorMessageBar: true,
                        orgCreationPolicyErrorMessage: reason.message
                    });
                    actionTelemetryLogger.captureError(reason.message);
                }
            );
        } else if (this._isGlobalPatCreationRestrictionPolicy(policyData)) {
            this.setState({ isGlobalPatPolicyUpdateInProgress: true });
            await source.updateGlobalPatCreationPolicy(this.context.pageContext, policyData).then(
                result => {
                    this.globalPatPolicyToggleEnabled.value = policyData.isPolicyEnabled === true;
                    this.setState({
                        globalPatPolicyData: policyData,
                        isGlobalPatPolicyUpdateInProgress: false,
                        showGlobalPatPolicyErrorMessageBar: false,
                        globalPatPolicyErrorMessage: ""
                    });
                },
                reason => {
                    this.setState({
                        isGlobalPatPolicyUpdateInProgress: false,
                        showGlobalPatPolicyErrorMessageBar: true,
                        globalPatPolicyErrorMessage: reason.message
                    });
                    actionTelemetryLogger.captureError(reason.message);
                }
            );
        } else if (this._isFullScopePatCreationRestrictionPolicy(policyData)) {
            this.setState({ isFullScopePatPolicyUpdateInProgress: true });
            await source.updateFullScopePatCreationPolicy(this.context.pageContext, policyData).then(
                result => {
                    this.fullScopePatPolicyToggleEnabled.value = policyData.isPolicyEnabled === true;
                    this.setState({
                        fullScopePatPolicyData: policyData,
                        isFullScopePatPolicyUpdateInProgress: false,
                        showFullScopePatPolicyErrorMessageBar: false,
                        fullScopePatPolicyErrorMessage: ""
                    });
                },
                reason => {
                    this.setState({
                        isFullScopePatPolicyUpdateInProgress: false,
                        showFullScopePatPolicyErrorMessageBar: true,
                        fullScopePatPolicyErrorMessage: reason.message
                    });
                    actionTelemetryLogger.captureError(reason.message);
                }
            );
        } else if (this._isEnforcePersonalTokenLifespanPolicy(policyData)) {
            this.setState({ isTokenLifespanPolicyUpdateInProgress: true });
            await source.updateTokenLifespanPolicy(this.context.pageContext, policyData).then(
                result => {
                    this.tokenLifespanPolicyToggleEnabled.value = policyData.isPolicyEnabled === true;
                    this.setState({
                        tokenLifespanPolicyData: policyData,
                        isTokenLifespanPolicyUpdateInProgress: false,
                        showTokenLifespanPolicyErrorMessageBar: false,
                        tokenLifespanPolicyErrorMessage: ""
                    });
                },
                reason => {
                    this.setState({
                        isTokenLifespanPolicyUpdateInProgress: false,
                        showTokenLifespanPolicyErrorMessageBar: true,
                        tokenLifespanPolicyErrorMessage: reason.message
                    });
                    actionTelemetryLogger.captureError(reason.message);
                }
            );
        } else if(this._isEnableLeakedPersonalAccessTokenAutoRevocationPolicy(policyData)) {
            this.setState({ isTokenAutoRevocationPolicyUpdateInProgress: true });
            await source.updateTokenAutoRevocationPolicy(this.context.pageContext, policyData).then(
                result => {
                    this.tokenAutoRevocationPolicyToggleEnabled.value = policyData.isPolicyEnabled === true;
                    this.setState({
                        tokenAutoRevocationPolicyData: policyData,
                        isTokenAutoRevocationPolicyUpdateInProgress: false,
                        showTokenAutoRevocationPolicyErrorMessageBar: false,
                        tokenAutoRevocationPolicyErrorMessage: ""
                    });
                },
                reason => {
                    this.setState({
                        isTokenAutoRevocationPolicyUpdateInProgress: false,
                        showTokenAutoRevocationPolicyErrorMessageBar: true,
                        tokenAutoRevocationPolicyErrorMessage: reason.message
                    });
                    actionTelemetryLogger.captureError(reason.message);
                }
            );
        }
    };

    private _downloadConnectedOrgs = async () => {
        this.setState({
            isDownloadInProgress: true
        });

        var actionTelemetryLogger = this._startActionCapture("Download Connected Orgs", {});
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        let tokens = await contributionService.getDataAsync<IOrganizationTokenData>(authTokenDataProviderId);
        var source = new OrganizationAadSource();
        await source.downloadConnectedOrgs(this.state.data.orgnizationTenantData.id, this.context.pageContext, tokens).then(
            result => {
                this.setState({ isDownloadInProgress: false });
                var fileName = format("Azure_DevOps_Organizations_In_{0}.csv", this.state.data.orgnizationTenantData.id);
                var blob = new Blob([result], { type: "text/plain" });

                if ((navigator as any).msSaveBlob) {
                    // IE 10+
                    (navigator as any).msSaveBlob(blob, fileName);
                } else {
                    var objectUrl = URL.createObjectURL(blob);
                    var a = document.createElement("a");
                    a.href = objectUrl;
                    a.download = fileName;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                }
            },
            reason => {
                actionTelemetryLogger.captureError(reason.message);
                this.setState({
                    isDownloadInProgress: false,
                    showDownloadErrorMessageBar: true,
                    downloadErrorMessage: reason.message
                });
            }
        );
    };

    protected _onMapIdentities = async (mappingIdentities: IUserMappingData[]) => {
        var identitiesToBeMapped = mappingIdentities.filter(obj => {
            return obj.status === true;
        });

        if (identitiesToBeMapped.length != 0) {
            this.setState({
                showResolvePanel: false,
                showMappingLoadingPanel: true,
                mappingUserInProgress: identitiesToBeMapped.length
            });
            var remainingDisconnectedUsers = this.state.disconnectedUsers;
            var failureMappingUsers = this.state.disconnectedUsers;
            var successMappingDescriptor: string[] = [];
            var failedMappingDescriptor: string[] = [];
            var actionTelemetryLogger = this._startActionCapture("Mapping Unresolved Users", {
                NumberOfUsersToBeMapped: identitiesToBeMapped.length
            });
            var userMappingFailureCustomModel: IOperationStatus[] = [];

            var source = new OrganizationAadSource();
            await Promise.all(
                identitiesToBeMapped.map(async userMapping => {
                    if (userMapping.mappingOriginId && userMapping.currentUserDescriptor) {
                        await source.mapUnresolvedUsers(this.context.pageContext, userMapping).then(
                            result => {
                                successMappingDescriptor.push(userMapping.currentUserDescriptor);
                            },
                            reason => {
                                failedMappingDescriptor.push(userMapping.currentUserDescriptor);
                                userMappingFailureCustomModel.push({
                                    name: userMapping.currentUserEmail,
                                    value: userMapping.mappingIdentityEmail,
                                    message: CleanErrorMessage(ParseErrorFromAPI(reason))
                                });
                                actionTelemetryLogger.captureError(ParseErrorFromAPI(reason));
                            }
                        );
                    }
                })
            ).then(result => {
                successMappingDescriptor.forEach(userDescriptor => {
                    remainingDisconnectedUsers = remainingDisconnectedUsers.filter(obj => {
                        return obj.descriptor !== userDescriptor;
                    });
                });

                failedMappingDescriptor.forEach(userDescriptor => {
                    failureMappingUsers = failureMappingUsers.filter(obj => {
                        return obj.descriptor === userDescriptor;
                    });
                });

                this.setState({
                    disconnectedUsers: remainingDisconnectedUsers,
                    showMappingLoadingPanel: false,
                    showSendInvitePanel: remainingDisconnectedUsers.length == 0 ? false : true,
                    numberOfSuccessfulMappedUser: successMappingDescriptor.length,
                    numberOfUnsuccessfulMappedUser: identitiesToBeMapped.length - successMappingDescriptor.length,
                    userResolveActionDone: remainingDisconnectedUsers.length == 0 ? true : false,
                    failureMappingUsers: failureMappingUsers,
                    showDisconnectedUserWarning: remainingDisconnectedUsers.length == 0 ? false : this.state.showDisconnectedUserWarning,
                    failureMappingUsersCustomModel: userMappingFailureCustomModel
                });
            });
        } else {
            this.setState({
                showResolvePanel: false,
                showSendInvitePanel: this.state.disconnectedUsers.length == 0 ? false : true,
                userResolveActionDone: this.state.disconnectedUsers.length == 0 ? true : false,
                numberOfSuccessfulMappedUser: 0,
                numberOfUnsuccessfulMappedUser: 0,
                failureMappingUsers: []
            });
        }
    };

    private _openAadDisconnectPanel = () => {
        this._startActionCapture("Show AAD Disconnect Panel", {});
        this.setState({ showAadDisconnectDialog: true });
    };

    private _dismissAadDisconnectPanel = () => {
        this.setState({ showAadDisconnectDialog: false });
    };

    private _dismissSendInvitePanel = () => {
        this.setState({
            showSendInvitePanel: false,
            userResolveActionDone: true,
            numberOfSuccessfulInvitedUser: 0,
            numberOfUnsuccessfulInvitedUser: 0,
            showDisconnectedUserWarning: this.state.disconnectedUsers.length > 0 ? true : false
        });
    };

    private _onOpenFailureMappingUserDialog = () => {
        this.setState({
            showFailureMappedUsersDialog: true
        });
    };

    private _onCloseFailureMappingUsersDialog = () => {
        this.setState({
            showFailureMappedUsersDialog: false
        });
    };

    private _onOpenFailureInvitingUsersDialog = () => {
        this.setState({
            showFailureInvitingUsersDialog: true
        });
    };

    private _onCloseFailureInvitingUsersDialog = () => {
        this.setState({
            showFailureInvitingUsersDialog: false
        });
    };

    protected _onSendInvite = async (userSelectedSendInvite: boolean) => {
        if (userSelectedSendInvite === true) {
            this.setState({
                showInvitationLoadingPanel: true,
                showSendInvitePanel: false
            });

            var actionTelemetryLogger = this._startActionCapture("Send AAD invitations", {
                NumberOfAADInvitations: this.state.disconnectedUsers.length
            });

            var remainingDisconnectedUsers = this.state.disconnectedUsers;
            //TODO: need to batch users by 100 if it's larger than 100
            var invitees: Invitee[] = [];
            for (var i = 0; i < this.state.disconnectedUsers.length; i++) {
                invitees.push({ userId: this.state.disconnectedUsers[i].id, message: "", status: InviteeStatus.None });
            }
            var source = new OrganizationAadSource();
            await source.sendInvitations(this.context.pageContext, invitees).then(
                result => {
                    this.setState({
                        showInvitationLoadingPanel: false,
                        userResolveActionDone: true,
                        showDisconnectedUserWarning: false
                    });
                    if (result) {
                        var successInvites = 0;
                        var unsuccessInvites = 0;
                        for (var i = 0; i < result.invitees.length; i++) {
                            if (result.invitees[i].status === InviteeStatus.Success) {
                                successInvites++;
                                remainingDisconnectedUsers = remainingDisconnectedUsers.filter(obj => {
                                    return obj.id !== result.invitees[i].userId;
                                });
                            } else {
                                unsuccessInvites++;
                                actionTelemetryLogger.captureError(result.invitees[i].message);
                            }
                        }

                        this.setState({
                            numberOfSuccessfulInvitedUser: successInvites,
                            numberOfUnsuccessfulInvitedUser: unsuccessInvites,
                            disconnectedUsers: remainingDisconnectedUsers
                        });
                    } else {
                        actionTelemetryLogger.captureError("No result returned from sendInvitations");
                        this.setState({
                            numberOfUnsuccessfulInvitedUser: this.state.disconnectedUsers.length
                        });
                    }
                },
                reason => {
                    this.setState({
                        showInvitationLoadingPanel: false,
                        numberOfUnsuccessfulInvitedUser: this.state.disconnectedUsers.length,
                        userResolveActionDone: true,
                        showDisconnectedUserWarning: false
                    });
                    actionTelemetryLogger.captureError(reason.message);
                }
            );
        } else {
            this.setState({
                showSendInvitePanel: false,
                userResolveActionDone: true,
                numberOfSuccessfulInvitedUser: 0,
                numberOfUnsuccessfulInvitedUser: 0,
                showDisconnectedUserWarning: this.state.disconnectedUsers.length > 0 ? true : false
            });
        }
    };

    private _onCloseAndSignout = () => {
        this._startActionCapture("Close AAD Dialog and Sign Out", {});
        this.setState({
            showAadDisconnectSuccessDialog: false,
            showAadConnectSuccessDialog: false
        });
        window.location.href = this.state.data.spsSignoutUrl;
    };

    private _onCloseDisconnectUserDialog = () => {
        this._startActionCapture("Close Unresolved User Dialog", {});
        this.setState({
            showDisconnectUserDialog: false
        });
    };

    private _onOpenDisconnectUserDialog = () => {
        this._startActionCapture("Open Unresolved User Dialog", {});
        this.setState({
            showDisconnectUserDialog: true
        });
    };

    private _onCloseUnsuccessAction = () => {
        this.setState({
            showAadConnectUnsuccessDialog: false,
            showAadDisconnectUnsuccessDialog: false
        });
    };

    protected _onUpdateUnresolvedUsers = (result: DisconnectedUsers) => {
        var showWarning = false;
        if (result.users && result.users.length > 0) {
            showWarning = true;
        }
        this.setState({
            disconnectedUsers: result.users,
            totalOrgUsers: result.orgUserCount,
            showTooManyUsersWarning: result.showTooManyUsersWarning,
            showDisconnectedUserWarning: showWarning
        });
    };

    private _onGetUnresolveUsers = async () => {
        var source = new OrganizationAadSource();
        var actionTelemetryLogger = this._startActionCapture("Get Unresolved User List", {});
        await source.getDisconnectedUsers(this.state.data.orgnizationTenantData.id, this.context.pageContext).then(
            result => {
                if (result) {
                    this._onUpdateUnresolvedUsers(result);
                } else {
                    actionTelemetryLogger.captureError("No result returned from getDisconnectedUsers");
                }
            },
            reason => {
                actionTelemetryLogger.captureError(reason.message);
            }
        );
    };

    private _onConnectToAad = async (newTenantId: string, newTenantName: string) => {
        this.setState({ showAadConnectPanel: false, updateAadInProgress: true, newOrgTenantName: newTenantName });

        var source = new OrganizationAadSource();
        var actionTelemetryLogger = this._startActionCapture("Connect Organization Tenant", {
            SelectedTenant: newTenantId,
            CurrentOrg: this.state.data.organizationId,
            NumberOfDisconnectUsers: this.state.disconnectedUsers.length
        });

        await source.updateAadTenant(newTenantId, this.context).then(
            result => {
                if (result) {
                    this.setState({ updateAadInProgress: false, showAadConnectSuccessDialog: true });
                } else {
                    actionTelemetryLogger.captureError("No result returned from updateAadTenant");
                    this.setState({ updateAadInProgress: false, showAadConnectUnsuccessDialog: true });
                }
            },
            reason => {
                actionTelemetryLogger.captureError(reason.message);
                this.setState({
                    updateAadInProgress: false,
                    showAadConnectUnsuccessDialog: true,
                    aadConnectError: CleanErrorMessage(reason.message)
                });
            }
        );
    };

    private _onDisconnectToAad = async () => {
        this.setState({ showAadDisconnectDialog: false, updateAadInProgress: true });

        var source = new OrganizationAadSource();
        var actionTelemetryLogger = this._startActionCapture("Disconnect Organization Tenant", {});

        await source.updateAadTenant(EmptyGuidString, this.context).then(
            result => {
                if (result) {
                    this.setState({ updateAadInProgress: false, showAadDisconnectSuccessDialog: true });
                } else {
                    actionTelemetryLogger.captureError("No result returned from updateAadTenant");
                    this.setState({ updateAadInProgress: false, showAadDisconnectUnsuccessDialog: true });
                }
            },
            reason => {
                actionTelemetryLogger.captureError(reason.message);
                this.setState({
                    updateAadInProgress: false,
                    showAadDisconnectUnsuccessDialog: true,
                    aadDisconnectError: CleanErrorMessage(reason.message)
                });
            }
        );
    };

    public componentDidMount(): void {
        super.componentDidMount();

        const performanceService: IVssPerformanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.markTimeToInteractive(Area.Admin, Feature.OrganizationAad);

        this._FetchTenantData();
        this._updateDelayLoad();
    }

    private async _updateDelayLoad() {
        var contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        var newdata = await contributionService.getDataAsync<IOrganizationAadDelayLoadSettingsData>(delayLoadProviderId)!;

        if (newdata) {
            if (newdata.orgCreationPolicy) {
                this.orgPolicyToggleEnabled.value = newdata.orgCreationPolicy.isPolicyEnabled === true;
            } else {
                this.orgPolicyToggleEnabled.value = false;
            }

            if (newdata.globalTokenPolicy) {
                this.globalPatPolicyToggleEnabled.value = newdata.globalTokenPolicy.isPolicyEnabled == true;
            } else {
                this.globalPatPolicyToggleEnabled.value = false;
            }

            if (newdata.fullScopeTokenPolicy) {
                this.fullScopePatPolicyToggleEnabled.value = newdata.fullScopeTokenPolicy.isPolicyEnabled === true;
            } else {
                this.fullScopePatPolicyToggleEnabled.value = false;
            }

            if (newdata.tokenLifespanPolicy) {
                this.tokenLifespanPolicyToggleEnabled.value = newdata.tokenLifespanPolicy.isPolicyEnabled === true;
            } else {
                this.tokenLifespanPolicyToggleEnabled.value = false;
            }

            if(newdata.leakedTokenAutoRevocationPolicy) {
                this.tokenAutoRevocationPolicyToggleEnabled.value = newdata.leakedTokenAutoRevocationPolicy.isPolicyEnabled === true;
            } else {
                this.tokenAutoRevocationPolicyToggleEnabled.value = false;
            }
        }

        this.setState({
            orgPolicyData: newdata && newdata.orgCreationPolicy ? newdata.orgCreationPolicy : undefined
        });

        this.setState({
            globalPatPolicyData: newdata && newdata.globalTokenPolicy ? newdata.globalTokenPolicy : undefined
        });

        this.setState({
            fullScopePatPolicyData: newdata && newdata.fullScopeTokenPolicy ? newdata.fullScopeTokenPolicy : undefined
        });

        this.setState({
            tokenLifespanPolicyData: newdata && newdata.tokenLifespanPolicy ? newdata.tokenLifespanPolicy : undefined,
            tokenLifespanPendingMaxLifespanDays: newdata &&
                newdata.tokenLifespanPolicy &&
                newdata.tokenLifespanPolicy.properties ?
                newdata.tokenLifespanPolicy.properties[PolicyProperties.MaxPatLifespanInDays] :
                enforceTokenLifespanPolicyDefaultMaxLifespanDays.toString()
        });

        this.setState({
            tokenAutoRevocationPolicyData: newdata && newdata.leakedTokenAutoRevocationPolicy ? newdata.leakedTokenAutoRevocationPolicy : undefined
        })
    }

    private _FetchTenantData = async () => {
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        var tenantData = await contributionService.getDataAsync<IUserTenantsData>(userTenantDataProviderId)!;

        var hasUserTenant = false;
        if (tenantData && tenantData.userTenantData && tenantData.userTenantData.userTenants.length > 0) {
            hasUserTenant = true;
        }
        this.setState({ userTenantData: tenantData ? tenantData.userTenantData : undefined, hasUserTenant: hasUserTenant });
    };

    private _isOrgCreationRestrictionPolicy(policy: IAadTenantPolicy) {
        return (policy != null && policy.policyName == 'TenantPolicy.OrganizationCreationRestriction');
    }

    private _isGlobalPatCreationRestrictionPolicy(policy: IAadTenantPolicy) {
        return (policy != null && policy.policyName == 'TenantPolicy.RestrictGlobalPersonalAccessToken');
    }

    private _isFullScopePatCreationRestrictionPolicy(policy: IAadTenantPolicy) {
        return (policy != null && policy.policyName == 'TenantPolicy.RestrictFullScopePersonalAccessToken');
    }

    private _isEnforcePersonalTokenLifespanPolicy(policy: IAadTenantPolicy) {
        return (policy != null && policy.policyName == 'TenantPolicy.RestrictPersonalAccessTokenLifespan');
    }
    
    private _isEnableLeakedPersonalAccessTokenAutoRevocationPolicy(policy: IAadTenantPolicy) {
        return (policy != null && policy.policyName == 'TenantPolicy.EnableLeakedPersonalAccessTokenAutoRevocation');
    }
}

Components.add("organizationAad", OrganizationAadComponent);
